import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowRight, Store, MapPin, Globe, Package, Star, DollarSign,
  CheckCircle, XCircle, ShieldOff, Percent, Calendar, User, CreditCard,
  BarChart3, TrendingUp, Clock, ShoppingCart, Award, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { vendorsService } from '@/services/vendorsService'
import { productsService } from '@/services/productsService'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import StatusBadge from '@/components/shared/StatusBadge'
import ConfirmDialog from '@/components/shared/ConfirmDialog'
import { formatCurrency, formatDate, formatRelativeTime, getLocalizedField } from '@/utils/formatters'

// ── Small reusable info row ───────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, color = '#000666' }) => (
  <div className="flex items-start gap-3 py-3" style={{ borderBottom: '1px solid #F3F3F3' }}>
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
      style={{ background: `${color}10`, color }}>
      <Icon size={15} />
    </div>
    <div>
      <p className="text-xs text-gray-400 mb-0.5" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</p>
      <p className="text-sm font-bold text-gray-800" style={{ fontFamily: 'Cairo, sans-serif' }}>{value ?? '—'}</p>
    </div>
  </div>
)

// ── Stat mini card ────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-xl p-4 flex flex-col gap-2" style={{ background: `${color}08` }}>
    <Icon size={18} style={{ color }} />
    <p className="text-xl font-black" style={{ color, fontFamily: 'Cairo, sans-serif' }}>{value ?? '—'}</p>
    <p className="text-xs text-gray-500" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</p>
  </div>
)

export default function VendorDetailPage() {
  const { id: slug } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { page, limit, setPage } = usePagination(1, 12)

  const [actionDialog, setActionDialog] = useState({ open: false, type: null })
  const [commissionDialog, setCommissionDialog] = useState({ open: false, value: '' })
  const [rejectDialog, setRejectDialog] = useState({ open: false, reason: '' })

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', slug],
    queryFn: () => vendorsService.getById(slug),
    retry: 1,
  })

  const vendorId = vendor?.id

  const { data: vendorStats, isLoading: statsLoading } = useQuery({
    queryKey: ['vendor-stats', vendorId],
    queryFn: () => vendorsService.getStatsById(vendorId),
    enabled: !!vendorId,
    retry: 1,
  })

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['vendor-products', vendorId, page],
    queryFn: () => productsService.getAll({ vendorId, page, limit }),
    enabled: !!vendorId,
    retry: 1,
  })

  const products = productsData?.data || []
  const prodMeta = productsData?.meta || {}

  // ── Mutations ─────────────────────────────────────────────────────────────
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['vendor', slug] })
    qc.invalidateQueries({ queryKey: ['vendors'] })
    qc.invalidateQueries({ queryKey: ['vendors-pending'] })
  }

  const approveMut = useMutation({
    mutationFn: () => vendorsService.approve(vendorId),
    onSuccess: () => { toast.success('تم قبول البائع'); invalidate(); setActionDialog({ open: false, type: null }) },
    onError: () => toast.error('حدث خطأ'),
  })

  const rejectMut = useMutation({
    mutationFn: () => vendorsService.reject(vendorId, rejectDialog.reason),
    onSuccess: () => { toast.success('تم رفض الطلب'); invalidate(); setRejectDialog({ open: false, reason: '' }) },
    onError: () => toast.error('حدث خطأ'),
  })

  const suspendMut = useMutation({
    mutationFn: () => vendorsService.suspend(vendorId),
    onSuccess: () => { toast.success('تم إيقاف البائع'); invalidate(); setActionDialog({ open: false, type: null }) },
    onError: () => toast.error('حدث خطأ'),
  })

  const commissionMut = useMutation({
    mutationFn: () => vendorsService.updateCommission(vendorId, parseFloat(commissionDialog.value)),
    onSuccess: () => { toast.success('تم تحديث العمولة'); invalidate(); setCommissionDialog({ open: false, value: '' }) },
    onError: () => toast.error('يرجى إدخال نسبة صحيحة'),
  })

  if (isLoading) return <div className="flex items-center justify-center py-32"><LoadingSpinner /></div>
  if (!vendor) return <div className="text-center py-24 text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>البائع غير موجود</div>

  const lang = 'ar'
  const storeName = getLocalizedField(vendor.storeName, lang)
  const description = getLocalizedField(vendor.description, lang)
  const status = vendor.status

  return (
    <div className="space-y-6">

      {/* ── Back + Actions ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate('/vendors')}
          className="flex items-center gap-2 text-sm font-bold"
          style={{ color: '#000666', fontFamily: 'Cairo, sans-serif' }}
        >
          <ArrowRight size={16} />
          العودة إلى البائعين
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          {status === 'pending' && (
            <>
              <button onClick={() => setActionDialog({ open: true, type: 'approve' })}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(27,109,36,0.08)', color: '#1B6D24', fontFamily: 'Cairo, sans-serif' }}>
                <CheckCircle size={15} />قبول
              </button>
              <button onClick={() => setRejectDialog({ open: true, reason: '' })}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(186,26,26,0.08)', color: '#BA1A1A', fontFamily: 'Cairo, sans-serif' }}>
                <XCircle size={15} />رفض
              </button>
            </>
          )}
          {status === 'approved' && (
            <>
              <button onClick={() => navigate(`/products?vendorId=${vendorId}`)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(0,6,102,0.07)', color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
                <Package size={15} />عرض منتجات البائع
              </button>
              <button onClick={() => setCommissionDialog({ open: true, value: String(vendor.commissionRate ?? '') })}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(0,6,102,0.07)', color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
                <Percent size={15} />تعديل العمولة
              </button>
              <button onClick={() => setActionDialog({ open: true, type: 'suspend' })}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(186,26,26,0.08)', color: '#BA1A1A', fontFamily: 'Cairo, sans-serif' }}>
                <ShieldOff size={15} />إيقاف
              </button>
            </>
          )}
          {(status === 'rejected' || status === 'suspended') && (
            <button onClick={() => setActionDialog({ open: true, type: 'approve' })}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
              style={{ background: 'rgba(27,109,36,0.08)', color: '#1B6D24', fontFamily: 'Cairo, sans-serif' }}>
              <CheckCircle size={15} />إعادة قبول
            </button>
          )}
        </div>
      </div>

      {/* ── Main grid ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* ── Left panel: profile ─────────────────────────────────────────── */}
        <div className="xl:col-span-1 space-y-4">

          {/* Store card */}
          <div className="hg-card p-6">
            {/* Store header */}
            <div className="w-full h-24 rounded-xl mb-4 flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #000666 0%, #1A237E 100%)' }}>
              <Store size={32} className="text-white opacity-30" />
            </div>

            {/* Name */}
            <div className="flex flex-col items-center text-center -mt-10 mb-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-black border-4 border-white shadow-card mb-3"
                style={{ background: 'linear-gradient(135deg, #000666 0%, #1A237E 100%)' }}>
                {storeName?.charAt(0) || 'V'}
              </div>
              <h1 className="text-lg font-black text-gray-900" style={{ fontFamily: 'Cairo, sans-serif' }}>{storeName}</h1>
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                @{vendor.storeSlug}
              </p>
              <div className="mt-2">
                <StatusBadge status={status} type="vendor" lang="ar" />
              </div>
            </div>

            {description && (
              <p className="text-sm text-gray-500 text-center mb-4" style={{ fontFamily: 'Tajawal, sans-serif', lineHeight: 1.7 }}>
                {description}
              </p>
            )}

            <InfoRow icon={User}     label="صاحب المتجر"   value={vendor.user?.name} />
            <InfoRow icon={MapPin}   label="المدينة"        value={vendor.city} />
            <InfoRow icon={Globe}    label="الدولة"         value={vendor.country} />
            <InfoRow icon={Percent}  label="نسبة العمولة"  value={vendor.commissionRate != null ? `${vendor.commissionRate}%` : '—'} color="#FF6D00" />
            <InfoRow icon={Calendar} label="تاريخ التسجيل" value={formatDate(vendor.createdAt)} />
            {vendor.verifiedAt && (
              <InfoRow icon={CheckCircle} label="تاريخ القبول" value={formatDate(vendor.verifiedAt)} color="#1B6D24" />
            )}
            {vendor.rejectionReason && (
              <div className="mt-3 p-3 rounded-xl" style={{ background: 'rgba(186,26,26,0.06)' }}>
                <p className="text-xs font-bold mb-1" style={{ color: '#BA1A1A', fontFamily: 'Cairo, sans-serif' }}>سبب الرفض:</p>
                <p className="text-xs text-gray-600" style={{ fontFamily: 'Tajawal, sans-serif' }}>{vendor.rejectionReason}</p>
              </div>
            )}
          </div>

          {/* Plan card */}
          {vendor.plan && (
            <div className="hg-card p-5">
              <h3 className="font-black text-sm mb-3" style={{ color:'#000666', fontFamily:'Cairo, sans-serif', display:'flex', alignItems:'center', gap:8 }}>
                <CreditCard size={16} />
                الخطة الحالية
              </h3>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#000666,#1A237E)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <CreditCard size={18} style={{ color:'#fff' }} />
                </div>
                <div>
                  <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:14, color:'#1A1C1C' }}>
                    {getLocalizedField(vendor.plan.name)}
                  </div>
                  <div style={{ fontFamily:'Tajawal, sans-serif', fontSize:11, color:'#767683' }}>
                    {vendor.plan.maxProducts
                      ? `الحد الأقصى: ${vendor.plan.maxProducts} منتج`
                      : 'منتجات غير محدودة'}
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <div style={{ flex:1, padding:'8px 12px', borderRadius:10, background:'rgba(255,109,0,0.08)', textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'#FF6D00', fontFamily:'Tajawal, sans-serif', marginBottom:2 }}>عمولة الخطة</div>
                  <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:15, color:'#FF6D00' }}>
                    {vendor.plan.defaultCommissionRate != null ? `${vendor.plan.defaultCommissionRate}%` : '—'}
                  </div>
                </div>
                <div style={{ flex:1, padding:'8px 12px', borderRadius:10, background:'rgba(0,6,102,0.07)', textAlign:'center' }}>
                  <div style={{ fontSize:10, color:'#000666', fontFamily:'Tajawal, sans-serif', marginBottom:2 }}>عمولة البائع</div>
                  <div style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:15, color:'#000666' }}>
                    {vendor.commissionRate != null ? `${vendor.commissionRate}%` : '—'}
                  </div>
                </div>
              </div>
              {vendor.commissionRate != null && vendor.plan.defaultCommissionRate != null && vendor.commissionRate !== vendor.plan.defaultCommissionRate && (
                <div style={{ marginTop:8, padding:'6px 10px', borderRadius:8, background:'rgba(27,109,36,0.08)', fontFamily:'Tajawal, sans-serif', fontSize:11, color:'#1B6D24', textAlign:'center' }}>
                  هذه عمولة مخصصة — تختلف عن عمولة الخطة الافتراضية
                </div>
              )}
            </div>
          )}

          {/* Stats card */}
          <div className="hg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black text-sm text-gray-500" style={{ fontFamily: 'Cairo, sans-serif' }}>
                إحصائيات المتجر
              </h3>
              {statsLoading && <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#000666', borderTopColor: 'transparent' }} />}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                icon={DollarSign}
                label="إجمالي المبيعات"
                value={vendorStats ? formatCurrency(vendorStats.totalRevenue ?? vendorStats.totalSales ?? 0) : (vendor._count ? '—' : '—')}
                color="#1B6D24"
              />
              <StatCard
                icon={ShoppingCart}
                label="إجمالي الطلبات"
                value={vendorStats?.totalOrders ?? vendor._count?.orders ?? '—'}
                color="#000666"
              />
              <StatCard
                icon={Package}
                label="المنتجات النشطة"
                value={vendorStats?.activeProducts ?? vendor._count?.activeProducts ?? vendor._count?.products ?? '—'}
                color="#FF6D00"
              />
              <StatCard
                icon={Award}
                label="العمولة المستحقة"
                value={vendorStats ? formatCurrency(vendorStats.commissionEarned ?? vendorStats.totalCommission ?? 0) : '—'}
                color="#BA1A1A"
              />
              <StatCard
                icon={Star}
                label="متوسط التقييم"
                value={vendorStats?.averageRating != null ? `${Number(vendorStats.averageRating).toFixed(1)} ★` : (vendor._count?.reviews != null ? `${vendor._count.reviews} تقييم` : '—')}
                color="#FF6D00"
              />
              <StatCard
                icon={TrendingUp}
                label="متوسط قيمة الطلب"
                value={vendorStats ? formatCurrency(vendorStats.averageOrderValue ?? 0) : '—'}
                color="#767683"
              />
            </div>
          </div>
        </div>

        {/* ── Right panel: stats strip + products ────────────────────────── */}
        <div className="xl:col-span-2 space-y-4">

          {/* Financial summary strip */}
          {vendorStats && (
            <div className="hg-card p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={15} style={{ color: '#000666' }} />
                <h3 className="font-black text-sm" style={{ color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
                  ملخص الأداء المالي
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'إجمالي المبيعات',  value: formatCurrency(vendorStats.totalRevenue ?? vendorStats.totalSales ?? 0), color: '#1B6D24' },
                  { label: 'صافي المبيعات',    value: formatCurrency(vendorStats.netRevenue ?? vendorStats.vendorEarnings ?? 0), color: '#000666' },
                  { label: 'العمولة المستحقة', value: formatCurrency(vendorStats.commissionEarned ?? vendorStats.totalCommission ?? 0), color: '#FF6D00' },
                  { label: 'إجمالي الطلبات',   value: vendorStats.totalOrders ?? '—', color: '#767683' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl p-3 text-center" style={{ background: `${color}08` }}>
                    <p className="font-black text-base" style={{ color, fontFamily: 'Cairo, sans-serif' }}>{value}</p>
                    <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'Tajawal, sans-serif' }}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Products */}
          <div className="hg-card p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-black text-lg" style={{ color: '#000666', fontFamily: 'Cairo, sans-serif' }}>
              منتجات المتجر
            </h2>
            {prodMeta.total > 0 && (
              <span className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                {prodMeta.total} منتج
              </span>
            )}
          </div>

          {productsLoading ? (
            <div className="flex items-center justify-center py-16"><LoadingSpinner /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(0,6,102,0.06)', color: '#000666' }}>
                <Package size={28} />
              </div>
              <p className="text-gray-400 text-sm" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                لا توجد منتجات لهذا المتجر
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: '2px solid #F3F3F3' }}>
                      {['المنتج', 'الفئة', 'السعر', 'المخزون', 'الحالة', ''].map((h) => (
                        <th key={h}
                          className="pb-3 text-right text-xs font-bold text-gray-400 uppercase tracking-wide"
                          style={{ fontFamily: 'Tajawal, sans-serif', paddingLeft: 12, paddingRight: 4 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const name = getLocalizedField(product.name, lang)
                      const catName = getLocalizedField(product.category?.name, lang)
                      const img = product.images?.find(i => i.isPrimary)?.url || product.images?.[0]?.url
                      return (
                        <tr key={product.id} className="hover:bg-gray-50 transition-colors" style={{ borderBottom: '1px solid #F9F9F9' }}>
                          <td className="py-3 px-1">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0"
                                style={{ background: '#F3F3F3' }}>
                                {img
                                  ? <img src={img} alt={name} className="w-full h-full object-cover" />
                                  : <div className="w-full h-full flex items-center justify-center"><Package size={14} className="text-gray-400" /></div>
                                }
                              </div>
                              <div>
                                <p className="font-bold text-sm text-gray-800 line-clamp-1" style={{ fontFamily: 'Cairo, sans-serif' }}>{name}</p>
                                <p className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>SKU: {product.sku || '—'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-1">
                            <span className="text-xs text-gray-500 px-2 py-1 rounded-lg"
                              style={{ background: '#F3F3F3', fontFamily: 'Tajawal, sans-serif' }}>
                              {catName || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-1">
                            <span className="font-black text-sm" style={{ color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}>
                              {formatCurrency(product.price)}
                            </span>
                          </td>
                          <td className="py-3 px-1">
                            <span className={`text-xs font-bold ${product.stockQuantity <= 0 ? 'text-red-500' : product.stockQuantity <= (product.lowStockThreshold || 5) ? 'text-orange-500' : 'text-green-600'}`}
                              style={{ fontFamily: 'Cairo, sans-serif' }}>
                              {product.stockQuantity ?? '—'}
                            </span>
                          </td>
                          <td className="py-3 px-1">
                            <StatusBadge status={product.status} type="vendor" lang="ar" />
                          </td>
                          <td className="py-3 px-1">
                            {product.isFeatured && (
                              <span className="text-xs font-bold px-2 py-1 rounded-lg"
                                style={{ background: 'rgba(255,109,0,0.1)', color: '#FF6D00', fontFamily: 'Cairo, sans-serif' }}>
                                مميز ⭐
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {prodMeta.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid #F3F3F3' }}>
                  <span className="text-xs text-gray-400" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                    صفحة {prodMeta.page} من {prodMeta.totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!prodMeta.hasPrev}
                      className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-30"
                      style={{ background: '#F3F3F3' }}>
                      <ChevronRight size={16} />
                    </button>
                    <button onClick={() => setPage(p => p + 1)} disabled={!prodMeta.hasNext}
                      className="w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-30"
                      style={{ background: '#F3F3F3' }}>
                      <ChevronLeft size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </div>

      {/* ── Confirm dialog (approve / suspend) ────────────────────────────── */}
      <ConfirmDialog
        open={actionDialog.open}
        title={actionDialog.type === 'approve' ? 'قبول البائع' : 'إيقاف البائع'}
        message={actionDialog.type === 'approve'
          ? `هل تريد قبول "${storeName}"؟ سيتم إشعار البائع فور القبول.`
          : `هل تريد إيقاف "${storeName}"؟ لن يتمكن من الوصول إلى متجره.`}
        variant={actionDialog.type === 'suspend' ? 'danger' : 'warning'}
        loading={approveMut.isPending || suspendMut.isPending}
        onConfirm={() => actionDialog.type === 'approve' ? approveMut.mutate() : suspendMut.mutate()}
        onCancel={() => setActionDialog({ open: false, type: null })}
      />

      {/* ── Reject dialog ─────────────────────────────────────────────────── */}
      {rejectDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setRejectDialog({ open: false, reason: '' })}>
          <div className="hg-card p-6 w-full max-w-sm animate-in" onClick={e => e.stopPropagation()}>
            <h3 className="font-black text-base mb-3" style={{ fontFamily: 'Cairo, sans-serif', color: '#BA1A1A' }}>
              رفض طلب البائع
            </h3>
            <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              يرجى ذكر سبب الرفض — سيُرسل للبائع.
            </p>
            <textarea
              rows={3}
              className="hg-input w-full resize-none mb-4"
              placeholder="سبب الرفض..."
              value={rejectDialog.reason}
              onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
              style={{ fontFamily: 'Tajawal, sans-serif' }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => rejectMut.mutate()}
                disabled={!rejectDialog.reason.trim() || rejectMut.isPending}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white disabled:opacity-50"
                style={{ background: '#BA1A1A', fontFamily: 'Cairo, sans-serif' }}>
                {rejectMut.isPending ? '...' : 'تأكيد الرفض'}
              </button>
              <button
                onClick={() => setRejectDialog({ open: false, reason: '' })}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm"
                style={{ background: '#F3F3F3', color: '#454652', fontFamily: 'Cairo, sans-serif' }}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Commission dialog ─────────────────────────────────────────────── */}
      {commissionDialog.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setCommissionDialog({ open: false, value: '' })}>
          <div className="hg-card p-6 w-full max-w-xs animate-in" onClick={e => e.stopPropagation()}>
            <h3 className="font-black text-base mb-1" style={{ fontFamily: 'Cairo, sans-serif', color: '#000666' }}>
              تعديل نسبة العمولة
            </h3>
            <p className="text-sm text-gray-400 mb-4" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              العمولة الحالية: {vendor.commissionRate ?? '—'}%
            </p>
            <div className="relative mb-4">
              <input
                type="number" min="0" max="100" step="0.5"
                className="hg-input w-full"
                placeholder="0.0"
                value={commissionDialog.value}
                onChange={e => setCommissionDialog(d => ({ ...d, value: e.target.value }))}
                style={{ paddingLeft: 36, fontFamily: 'Cairo, sans-serif', direction: 'ltr', textAlign: 'left' }}
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold text-sm">%</span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => commissionMut.mutate()}
                disabled={!commissionDialog.value || commissionMut.isPending}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white disabled:opacity-50"
                style={{ background: '#000666', fontFamily: 'Cairo, sans-serif' }}>
                {commissionMut.isPending ? '...' : 'حفظ'}
              </button>
              <button
                onClick={() => setCommissionDialog({ open: false, value: '' })}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm"
                style={{ background: '#F3F3F3', color: '#454652', fontFamily: 'Cairo, sans-serif' }}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
